import {
  Chart,
  LineController,
  BarElement,
  CategoryScale,
  ChartData,
  PointElement,
  LineElement,
  LinearScale,
  Tooltip,
  TimeScale,
  TimeUnit,
} from 'chart.js';
import 'chartjs-adapter-date-fns'; // Import the date adapter
import { Chart as ReactChart } from 'react-chartjs-2';
import { TooltipModel } from 'chart.js/dist/types';
import { gtPlanar } from 'components/Typography';
import { formatValueToSixDecimals } from '@dapp/multi-lib/lib/formatters';

// Register necessary components
Chart.register(
  LineController,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  TimeScale
);

const getOrCreateTooltip = (chart: Chart) => {
  let tooltipEl = chart.canvas.parentNode!.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.style.background = 'transparent';
    // tooltipEl.style.borderRadius = '3px';
    // tooltipEl.style.color = 'white';
    tooltipEl.style.opacity = '1';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.transform = 'translate(-50%, 0)';
    tooltipEl.style.transition = 'all .1s ease';
    chart.canvas.parentNode!.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const externalTooltipHandler = (context: {
  chart: Chart;
  tooltip: TooltipModel<'line'>;
}) => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return;
  }

  // Set Text
  if (tooltip.body) {
    const label = tooltip.dataPoints[0].label;
    let value = '';
    if (tooltip.dataPoints[0].dataset.label === 'Value') {
      value = `$${formatValueToSixDecimals(Number(tooltip.dataPoints[0].raw))}`;
    } else {
      value = `${tooltip.dataPoints[0].raw}%`;
    }

    const el = document.createElement('div');
    el.innerHTML = `<div class='${gtPlanar.className}'><p class='text-white'>${value}</p><p class='text-[#BFBFBF]'>${label}</p></div>`;

    // Remove old children
    while (tooltipEl.firstChild) {
      tooltipEl.firstChild.remove();
    }

    tooltipEl.appendChild(el);
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Display, position, and set styles for font
  tooltipEl.style.opacity = '1';
  tooltipEl.style.left = positionX + tooltip.caretX + 'px';
  tooltipEl.style.top = positionY + tooltip.caretY + 'px';
  // tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.style.padding =
    tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';
};

interface ChartLineProps {
  data: number[];
  unit: TimeUnit;
  labels: number[] | string[];
  maxTicksLimit: number;
  denomination?: string;
  yLabelPrecision: number;
}

export const ChartLine = (props: ChartLineProps) => {
  const { data, labels, maxTicksLimit, unit, denomination, yLabelPrecision } =
    props;

  const _dataset: ChartData = {
    labels: labels,
    datasets: [
      {
        label: denomination === '%' ? 'Performance' : 'Value',
        fill: false,
        data: data,
        backgroundColor: '#9DB178',
        borderColor: '#9DB178',
        borderWidth: 5,
        tension: 0.4,
      },
    ],
  };

  return (
    <ReactChart
      type='line'
      data={_dataset}
      options={{
        devicePixelRatio: 2,
        maintainAspectRatio: true,
        responsive: true,
        plugins: {
          tooltip: {
            enabled: false,
            mode: 'index',
            intersect: false,
            external: externalTooltipHandler,
          },
        },
        interaction: {
          intersect: false,
        },
        elements: {
          point: {
            radius: 0,
            hoverRadius: 5,
          },
        },
        scales: {
          x: {
            border: {
              display: false,
            },
            type: 'time', // Set the x-axis to use time scale
            time: {
              unit: unit,
              displayFormats: {
                hour: 'HH:mm', // Format for hours
              },
            },
            grid: {
              display: false,
            },
            display: true,
            title: {
              display: true,
            },
            ticks: {
              maxTicksLimit: maxTicksLimit,
            },
          },
          y: {
            border: {
              display: false,
            },
            grid: {
              display: false,
            },
            position: 'right',
            display: true,
            title: {
              display: false,
              text: 'Value',
            },
            ticks: {
              display: true,
              maxTicksLimit: 5,
              callback: (value) => {
                if (typeof value !== 'number') {
                  return value;
                }

                if (denomination === '%') {
                  return `${value.toFixed(yLabelPrecision)}%`;
                }

                return `${denomination}${value.toFixed(yLabelPrecision)}`;
              },
            },
            suggestedMin: Math.min(...data),
            suggestedMax: Math.max(...data),
          },
        },
      }}
    />
  );
};
